// NOTE: This component is still used by LoglineSessionHeader for history functionality.
// Unlike other LogLine components, this one is NOT deprecated.
// History rows come from the shared Note history (note_history, kind = logline).

import React, { useState, useEffect } from 'react';
import { loglineApi } from '../../api/logline';
import { savedOnLabel } from '../../utils/noteHistory';
import { CHANGE_LOG_EMPTY, CHANGE_LOG_ERROR } from '../../utils/writerCopy';
import './LogLine.css';

const RESTORE_ERROR = 'Could not restore this save.';

/**
 * LogLineHistory - Modal for viewing and restoring past loglines.
 *
 * Used by: LoglineSessionHeader (Session/LoglineSessionHeader.jsx)
 * Each row is one save, with its time. There is no numbered sequence.
 */
export default function LogLineHistory({
  storyId,
  isOpen,
  onClose,
  onRestore
}) {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    if (!isOpen || !storyId) return;

    const loadHistory = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await loglineApi.getHistory(storyId);
        setHistory(response?.data || []);
      } catch (err) {
        console.error('Failed to load history:', err);
        setError(CHANGE_LOG_ERROR);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [isOpen, storyId]);

  const handleRestore = async (historyId, logLine) => {
    setRestoringId(historyId);
    try {
      await loglineApi.restore(storyId, historyId);
      onRestore?.(logLine);
      onClose();
    } catch (err) {
      console.error('Failed to restore:', err);
      setError(RESTORE_ERROR);
    } finally {
      setRestoringId(null);
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'generated': return 'Generated';
      case 'workshop': return 'Workshop';
      case 'manual': return 'Manual edit';
      default: return source;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="logline-modal-overlay" onClick={onClose}>
      <div className="logline-modal" onClick={(e) => e.stopPropagation()}>
        <div className="logline-modal__header">
          <h3 className="logline-modal__title">Log Line History</h3>
          <button className="logline-modal__close" onClick={onClose}>
            X
          </button>
        </div>

        <div className="logline-modal__content">
          {isLoading ? (
            <div className="logline-history--loading">
              <div className="logline-loading-spinner" />
              <span>Opening the change log…</span>
            </div>
          ) : error ? (
            <div className="logline-history--error">
              {error}
            </div>
          ) : history.length === 0 ? (
            <div className="logline-history--empty">
              {CHANGE_LOG_EMPTY}
            </div>
          ) : (
            <div className="logline-history">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className={`logline-history__entry ${entry.is_current ? 'logline-history__entry--current' : ''}`}
                >
                  <div className="logline-history__meta">
                    <span className="logline-history__date">
                      {savedOnLabel(entry.created_at) || 'Unknown date'}
                    </span>
                    {entry.is_current ? (
                      <span className="logline-history__label">Current</span>
                    ) : null}
                  </div>

                  <p className="logline-history__text">{entry.log_line}</p>

                  <div className="logline-history__footer">
                    <span className="logline-history__source">
                      Source: {getSourceLabel(entry.source)}
                    </span>

                    <div className="logline-history__actions">
                      <button
                        className="logline-btn logline-btn--secondary logline-history__copy-btn"
                        onClick={() => handleCopy(entry.log_line, entry.id)}
                      >
                        {copiedId === entry.id ? 'Copied!' : 'Copy'}
                      </button>
                      {!entry.is_current && (
                        <button
                          className="logline-btn logline-btn--secondary logline-history__restore-btn"
                          onClick={() => handleRestore(entry.id, entry.log_line)}
                          disabled={restoringId === entry.id}
                        >
                          {restoringId === entry.id ? 'Restoring...' : 'Restore'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
